import { FiscalLawEnum } from '@app/services/funnel/estate/funnel-bien-locatif.service';

const FISCALITY_ORDER: string[] = [
  FiscalLawEnum.FONCIER,
  FiscalLawEnum.MICRO_FONCIER,
  FiscalLawEnum.BIC,
  FiscalLawEnum.MICRO_BIC,
  FiscalLawEnum.MICRO_BIC_TOURISME_NON_CLASSE,
  FiscalLawEnum.MICRO_BIC_TOURISME_CLASSE,
];

const FISCALITY_LABELS: Record<string, string> = {
  [FiscalLawEnum.FONCIER]: 'Foncier',
  [FiscalLawEnum.MICRO_FONCIER]: 'Micro-Foncier',
  [FiscalLawEnum.BIC]: 'BIC',
  [FiscalLawEnum.MICRO_BIC]: 'Micro-BIC longue durée',
  [FiscalLawEnum.MICRO_BIC_TOURISME_CLASSE]: 'Micro-BIC tourisme classé',
  [FiscalLawEnum.MICRO_BIC_TOURISME_NON_CLASSE]:
    'Micro-BIC tourisme non classé',
};

const MICRO_REGIMES: string[] = [
  FiscalLawEnum.MICRO_FONCIER,
  FiscalLawEnum.MICRO_BIC,
  FiscalLawEnum.MICRO_BIC_TOURISME_CLASSE,
  FiscalLawEnum.MICRO_BIC_TOURISME_NON_CLASSE,
];

/** Régimes renvoyés par le back, dans l'ordre d'affichage du comparatif ; un régime inconnu du comparatif vient en dernier. */
export function orderFiscalities<T extends { type: string }>(
  fiscalities: T[] | undefined,
): T[] {
  const rank = (type: string) => {
    const index = FISCALITY_ORDER.indexOf(type);
    return index === -1 ? FISCALITY_ORDER.length : index;
  };
  return [...(fiscalities || [])].sort((a, b) => rank(a.type) - rank(b.type));
}

export function fiscalityLabel(type: string): string {
  return FISCALITY_LABELS[type] ?? type;
}

export function isMicroRegime(type: string): boolean {
  return MICRO_REGIMES.includes(type);
}

export function comparisonOverhead(fiscality: {
  actualFiscalLaw?: boolean;
  ceilingExceeded?: boolean;
}): string | undefined {
  const labels = [
    fiscality.actualFiscalLaw ? 'Solution actuelle' : undefined,
    fiscality.ceilingExceeded ? 'Plafond micro-BIC dépassé' : undefined,
  ].filter(label => !!label);
  return labels.length ? labels.join(' · ') : undefined;
}

/** Lignes d'une colonne du comparatif, à partir d'un régime calculé par le back. */
export function comparisonCells(fiscality: any) {
  const micro = isMicroRegime(fiscality.type);
  const realCharges =
    fiscality.type == FiscalLawEnum.FONCIER ||
    fiscality.type == FiscalLawEnum.BIC;
  const cell = (label: string, value: any, extra: object = {}) => ({
    label,
    type: 'currency',
    value,
    prefix: null,
    suffix: '',
    checked: false,
    optimize: fiscality.optimize,
    ...extra,
  });

  return [
    cell('Loyers', fiscality.loyer, { color: '#A3E9B2' }),
    cell('Abattement', micro ? fiscality.charge : null, { checked: micro }),
    cell('Charges', realCharges ? fiscality.charge : null),
    cell('Taxe foncière', fiscality.taxeFonciere),
    cell("Intérêts d'emprunt", fiscality.interet, {
      labelTooltip: "Intérêts d'emprunt",
    }),
    cell('Amortissement', fiscality.amortissement, {
      checked: fiscality.type == FiscalLawEnum.BIC,
      labelTooltip: 'Amortissement',
    }),
    cell('Résultat fiscal', fiscality.resultatFiscal, {
      labelTooltip: 'Résultat fiscal',
    }),
    cell('Impôts', fiscality.impot, { labelTooltip: 'Impôts' }),
    cell('Gains réels', fiscality.gain, {
      checked: fiscality.optimize,
      labelTooltip: 'Gains réels',
    }),
  ];
}
